/**
 * Implementation primitives for the snapshot layer — source +
 * layer registration, paint-expression composition, GeoJSON
 * conversion. Split from :file:`snapshot-layer.tsx` so the
 * ``react-refresh/only-export-components`` lint rule stays
 * satisfied (the ``.tsx`` file exports the React component
 * exclusively).
 *
 * Cross-references:
 *   - REQ-UI-002 (WebGL marker rendering).
 *   - REQ-UI-003 / REQ-UI-004.
 *   - ADR-016 §3 / §4.
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 */
import type {
  Map as MapLibreMap,
  GeoJSONSource,
  ExpressionSpecification,
  FilterSpecification,
} from "maplibre-gl";
import {
  STATUS_FALLBACK_HEX_MAP,
  HEALTH_STATE,
  platformSingleColorOrNull,
  resolveStatusColor,
  type PlatformColorConfig,
} from "@/map/marker-styling";
import {
  SELECTION_RING_STROKE_OPACITY,
  SELECTION_RING_STROKE_WIDTH,
  getSelectionRingColor,
  selectionRingRadiusExpression,
} from "@/map/selection-ring";
import {
  glyphIconImage,
  iconSizeExpression,
  normalizeGlyphId,
  registerGlyphIcons,
} from "@/map/glyphs";
import { isMapUsable } from "@/map/map-ready";
import { type SnapshotResponse, type SnapshotItem, hasMapPosition } from "@/map/snapshot-types";
import { CLUSTER_MAX_ZOOM, CLUSTER_RADIUS_PX, clusterProperties } from "@/map/cluster-styling";

/**
 * MapLibre GeoJSON source ID for the snapshot.
 *
 * Exported so the Playwright e2e + the Vitest layer-mount test
 * can probe the source via ``map.getSource(...)`` without
 * threading the ID through props.
 */
export const SNAPSHOT_SOURCE_ID = "meridian-snapshot-source";

/**
 * MapLibre circle-layer ID for the snapshot.
 *
 * Same exported-constant pattern as :const:`SNAPSHOT_SOURCE_ID`.
 */
export const SNAPSHOT_LAYER_ID = "meridian-snapshot-layer";

/**
 * MapLibre symbol-layer ID for the per-glyph display-name label
 * (centered below the marker). Same exported-constant pattern as
 * :const:`SNAPSHOT_LAYER_ID`.
 */
export const SNAPSHOT_LABEL_LAYER_ID = "meridian-snapshot-label-layer";

/**
 * MapLibre circle-layer ID for the selected-platform highlight ring
 * (REQ-UI-051; storyboard Flow Q §"Frame 2"). Mirrors the detection glyph's
 * highlight layer (:const:`detection-layer-impl.DETECTIONS_HIGHLIGHT_LAYER_ID`)
 * and shares the snapshot source.
 */
export const SNAPSHOT_HIGHLIGHT_LAYER_ID = "meridian-snapshot-highlight-layer";

/**
 * Sentinel producer_id the highlight filter uses when nothing is selected —
 * a value no real producer UUID equals, so the ring paints nothing.
 */
const HIGHLIGHT_NONE_SENTINEL = " __no_selected_platform__";

/** Glyph-label text size (px) — matches design-system ``--text-meta``. */
export const SNAPSHOT_LABEL_SIZE_PX = 11;

/** Glyph-label halo width (px) — lifts the name off the map tiles. */
export const SNAPSHOT_LABEL_HALO_WIDTH_PX = 1.5;

// Fallback label colors (dark-first per design-system 2026-05-26) for
// jsdom / SSR where getComputedStyle returns empty — same operator-
// visibility rationale as STATUS_FALLBACK_HEX_MAP.
const LABEL_TEXT_FALLBACK_HEX = "#e8eaf0"; // --text-primary (dark)
const LABEL_HALO_FALLBACK_HEX = "#0f1117"; // --bg-base (dark)

/**
 * Resolve a design-system color token to a MapLibre-valid color string.
 *
 * The Appaloosa base-palette tokens (``--text-primary``, ``--bg-base``,
 * …) store colors as **bare HSL channels** — e.g. ``225 21% 93%`` — so
 * the rest of the app can compose them as ``hsl(var(--text-primary))`` in
 * CSS (styles/globals.css). MapLibre's ``paint`` color properties need a
 * *complete* color, so a bare ``"225 21% 93%"`` is rejected with
 * ``color expected, "225 21% 93%" found`` and the whole layer fails to
 * register (the original PR #297 defect: the label layer silently never
 * mounted, so glyph names never rendered). Wrap the resolved channels in
 * ``hsl(...)`` to hand MapLibre a valid color.
 *
 * Unlike the ``--status-*`` tokens (which are stored as hex and so feed
 * :func:`getStatusColor` directly), these base-palette tokens are always
 * the bare-channel form, so the wrap is unconditional on a resolved value.
 * The ``fallback`` is already a complete hex string (jsdom / SSR path).
 */
export function resolveCssVarColor(name: string, fallback: string): string {
  if (typeof document === "undefined") {
    return fallback;
  }
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return resolved === "" ? fallback : `hsl(${resolved})`;
}

/**
 * Theme-aware glyph-label text color (``--text-primary``). Read at
 * paint time so Stream B's theme flip takes effect without re-mounting
 * the layer (mirrors :func:`getStatusColor`). REQ-UI-040 / REQ-UI-046.
 */
export function getLabelTextColor(): string {
  return resolveCssVarColor("--text-primary", LABEL_TEXT_FALLBACK_HEX);
}

/**
 * Theme-aware glyph-label halo color (``--bg-base``) — outlines the
 * text against the map tiles in both themes.
 */
export function getLabelHaloColor(): string {
  return resolveCssVarColor("--bg-base", LABEL_HALO_FALLBACK_HEX);
}

/**
 * Zoom → radius interpolation stops. Per Flow 02 §"Map markers and
 * glyphs" the marker stays visible at low zoom (≈ readable at z4–z6
 * over the CONUS framing) and grows for entity-detail readability
 * at z10+.
 *
 * Format matches MapLibre's ``interpolate`` expression spec:
 * ``[zoom_level, radius_px]`` pairs.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 */
export const MARKER_RADIUS_ZOOM_STOPS: ReadonlyArray<readonly [number, number]> = [
  [0, 3],
  [4, 4],
  [7, 4],
  [10, 6],
  [14, 8],
];

/**
 * Convert a :class:`SnapshotResponse` into a GeoJSON
 * ``FeatureCollection`` of point features.
 *
 * Each feature's geometry is the Producer position; its
 * ``properties`` carry ``producer_id`` + ``health_state`` so the
 * paint expression can key on them.
 *
 * Exported for unit testability — the test can verify that one
 * snapshot item maps to one feature with the expected coordinates
 * + properties.
 *
 * Cross-references:
 *   - REQ-UI-003.
 */
export function snapshotToFeatureCollection(
  snapshot: SnapshotResponse,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: "FeatureCollection",
    // A snapshot row always carries a position (the BFF viewport query excludes
    // NULL-position status), so filtering here is defensive type-safety for the
    // now-nullable StatusItem position (REQ-INGEST-044): a location-less status
    // has no map marker — it surfaces only in the Entity Details STATUS panel.
    features: snapshot.items.filter(hasMapPosition).map((item) => snapshotItemToFeature(item)),
  };
}

/**
 * Convert a single :class:`SnapshotItem` to a GeoJSON
 * ``Feature<Point>``.
 *
 * Properties carry the minimal set the paint expression needs;
 * detail-panel surfaces (M6) pull the full record from a separate
 * Producer-detail query rather than dragging every field through
 * the map's feature properties.
 */
function snapshotItemToFeature(
  item: SnapshotItem & { latitude_deg: number; longitude_deg: number },
): GeoJSON.Feature<GeoJSON.Point> {
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [item.longitude_deg, item.latitude_deg],
    },
    properties: {
      producer_id: item.producer_id,
      health_state: item.health_state,
      // CDS-arrival provenance marker (issue #278, REQ-UI-014) — true when
      // the producer's data crossed the CDS. The paint keys on this.
      crossed_cds: item.source_network != null && item.source_network !== "",
      // Operator-facing label rendered below the glyph by the label symbol
      // layer (producer_id is the canonical UUID). Empty string when the
      // BFF snapshot didn't resolve a registry name → no label placed.
      display_name: item.display_name ?? "",
    },
  };
}

/**
 * Compose the MapLibre ``match`` expression for ``circle-color``
 * keyed on the feature's ``health_state`` property.
 *
 * MapLibre evaluates the expression for every feature at paint
 * time — this avoids a client-side filter pass per the design
 * (ADR-016 §4 WebGL rendering at scale).
 *
 * The expression is evaluated against the **resolved** hex value
 * for each status (via :func:`getStatusColor`) so the layer paint
 * picks up the active theme's variable when Stream B's PR
 * lands — the layer's ``paint`` is re-applied via
 * :meth:`Map.setPaintProperty` on theme flip.
 *
 * Falls back to the design-system literals if :func:`getStatusColor`
 * can't read the CSS variable (test environment).
 *
 * # Profile single-colour mode (inc6b — REQ-UI-086)
 *
 * When the active Profile's ``map.platform.color_mode`` is ``"single"``
 * (``config.mode``), every marker paints the Profile's
 * ``single_color`` regardless of ``health_state`` — the expression collapses to
 * the flat resolved hex (:func:`marker-styling.platformSingleColorOrNull`). The
 * default ``"by_status"`` keeps the historical ``match`` on ``health_state``.
 * The colour is read at composition time exactly as before, so the theme-flip /
 * Profile-Apply re-paint path (:func:`attachSnapshotLayer`'s existing-source
 * branch calls ``setPaintProperty``) picks up the active-theme hue.
 *
 * # Per-status overrides (inc-items 4/5 — REQ-UI-086)
 *
 * In ``by_status`` mode each status hex is resolved via
 * :func:`marker-styling.resolveStatusColor`, which returns the operator's
 * ``map.platform.status_colors`` override (keyed by the :type:`MarkerStatus` id)
 * when present and the ``--status-*`` token otherwise. An override change
 * re-composes the expression and the existing-source re-paint path applies it in
 * place (``setPaintProperty``) — no teardown, so live SSE positions stay put.
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-045 (theme seam).
 *   - REQ-UI-046 (semantic distinguishability across themes).
 *   - REQ-UI-086 (platform colour mode + status overrides); ADR-033 §9.2.
 */
function composeColorExpression(platformColor?: PlatformColorConfig): ExpressionSpecification {
  // Profile ``single`` mode → flat colour for every marker (REQ-UI-086).
  const single = platformSingleColorOrNull(platformColor);
  if (single !== null) {
    return single as unknown as ExpressionSpecification;
  }

  // Resolved colors per status — override-first (``status_colors``), else the
  // live ``--status-*`` token. Read once at composition; the theme-flip /
  // override / Profile-Apply re-paint path re-composes the expression and calls
  // ``setPaintProperty`` on the layer to apply the new values.
  const overrides = platformColor?.statusColors;
  const healthyColor = resolveStatusColor("healthy", overrides);
  const degradedColor = resolveStatusColor("degraded", overrides);
  const unhealthyColor = resolveStatusColor("unhealthy", overrides);
  const unknownColor = resolveStatusColor("unknown", overrides);

  return [
    "match",
    ["get", "health_state"],
    HEALTH_STATE.HEALTHY,
    healthyColor,
    HEALTH_STATE.DEGRADED,
    degradedColor,
    HEALTH_STATE.OFFLINE,
    unhealthyColor,
    HEALTH_STATE.UNSPECIFIED,
    unknownColor,
    HEALTH_STATE.UNKNOWN,
    unknownColor,
    // Fallback for unrecognised values — same as the
    // marker-styling guard's degrade path.
    unknownColor,
  ];
}

/**
 * Per-feature **icon**-opacity expression for the platform glyph (GLYPHS
 * batch). Pre-glyphs the marker was a circle that rendered ``unknown`` as an
 * *open* (stroke-only) circle via ``circle-opacity: 0`` + a visible stroke
 * (Flow 02 §"Map markers and glyphs"). An SDF glyph is a solid filled shape
 * with no separate stroke channel, so the open-circle treatment can't carry
 * over verbatim. To keep an ``unknown`` platform **visually distinct yet still
 * visible** (operator-visibility — a thin outline that an SDF can't reproduce
 * would otherwise become a fully-painted shape), ``unknown`` is rendered at a
 * reduced opacity (a "ghosted" glyph) rather than fully opaque; every other
 * state stays fully opaque. The grey ``--status-unknown`` tint still carries the
 * primary distinction.
 *
 * NOTE (§5.11 visual pass): the exact ghosting opacity + whether ``unknown``
 * wants a different affordance entirely (e.g. a hollow ring overlay) is a
 * visual call — flagged for the end-pass review.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md
 *     §"Map markers and glyphs".
 *   - REQ-UI-086 (platform glyph) / CLAUDE.md §8.1 (operator-visibility).
 */
function composeIconOpacityExpression(): ExpressionSpecification {
  return [
    "match",
    ["get", "health_state"],
    HEALTH_STATE.UNSPECIFIED,
    UNKNOWN_GLYPH_OPACITY,
    HEALTH_STATE.UNKNOWN,
    UNKNOWN_GLYPH_OPACITY,
    1,
  ];
}

/** Reduced opacity for an ``unknown``-status platform glyph — "ghosted" so it
 *  stays visible (unlike the pre-glyphs invisible-fill open circle) while still
 *  reading as not-fully-reported. Tunable in the §5.11 visual pass. */
const UNKNOWN_GLYPH_OPACITY = 0.5;

/**
 * Filter for the selection-highlight ring: the un-clustered feature whose
 * ``producer_id`` matches the selection (``null`` → the sentinel, so the
 * ring paints nothing). The ``!has point_count`` clause keeps the ring off
 * cluster bubbles — a platform collapsed into a cluster has no individual
 * marker to ring (REQ-UI-011).
 */
function platformHighlightFilter(producerId: string | null): FilterSpecification {
  return [
    "all",
    ["!", ["has", "point_count"]],
    ["==", ["get", "producer_id"], producerId ?? HIGHLIGHT_NONE_SENTINEL],
  ];
}

/**
 * Register the source + layer on the live map. Idempotent — safe
 * to call multiple times; subsequent calls update the data on the
 * already-registered source.
 *
 * Exported for unit testability. The :class:`SnapshotLayer`
 * component (in :file:`snapshot-layer.tsx`) owns the React-effect
 * lifecycle around this; tests call this directly against a mock
 * map.
 *
 * # Profile platform colour + glyph (inc6b + GLYPHS — REQ-UI-086)
 *
 * ``platformColor`` carries the active Profile's ``map.platform`` colour mode +
 * single colour (ADR-033 §9.2); ``platformGlyph`` carries its
 * ``map.platform.glyph`` shape (GLYPHS batch; default ``triangle``). The
 * individual-marker layer is a **``symbol`` layer painting an SDF glyph** — the
 * chosen shape (``icon-image``) tinted by the colour expression (``icon-color``)
 * — so the shape is configurable while the inc6b colour modes are unchanged.
 * The **cluster** bubbles are a separate layer (:file:`cluster-layer.tsx`) and
 * stay circles. On the **first** attach it registers the glyph SDF icons +
 * composes the symbol layout/paint; on the **existing-source** path (a data
 * update, a theme flip, or a Profile Apply) it re-applies the icon image + the
 * colour via :meth:`Map.setLayoutProperty` / :meth:`Map.setPaintProperty` so a
 * shape/mode/colour change — or the active-theme status hex — repaints the live
 * layer without a teardown (mirrors :func:`use-map-theme.applyMapTheme`'s
 * targeted-paint discipline). Omit the args for the legacy ``by_status`` +
 * ``triangle`` behaviour; an unknown glyph id falls back to ``circle``.
 *
 * Cross-references:
 *   - REQ-UI-002 / REQ-UI-003 / REQ-UI-086.
 */
export function attachSnapshotLayer(
  map: MapLibreMap,
  snapshot: SnapshotResponse,
  platformColor?: PlatformColorConfig,
  platformGlyph?: string,
): void {
  // No-op safely when the map is removed / mid-teardown — the ``getSource`` /
  // ``getLayer`` calls below dereference ``map.style`` and would otherwise
  // throw during the "Reset to profile" dockview rebuild (see
  // :file:`map/map-ready.ts`). A still-*loading* map is usable: MapLibre
  // queues these source/layer ops until the style settles, so an attach that
  // races the load window is applied, not dropped. CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  const featureCollection = snapshotToFeatureCollection(snapshot);

  // Ensure the glyph SDF icons exist before the symbol layer references them
  // (idempotent; safe regardless of map-load / attach ordering).
  registerGlyphIcons(map);

  const existingSource = map.getSource(SNAPSHOT_SOURCE_ID) as GeoJSONSource | undefined;
  if (existingSource) {
    // A genuine snapshot DATA update (pan/zoom refetch). Replace the source
    // data, then re-apply the glyph image + colour in place so a concurrent
    // theme flip / Profile change carries through (REQ-UI-086 / REQ-UI-040).
    //
    // IMPORTANT (the colour-toggle flicker fix): a colour-/glyph-ONLY change is
    // NOT routed here — it goes through :func:`reapplySnapshotPlatformStyle`,
    // which never touches the source data. Re-running ``setData`` on a
    // colour-only change would overwrite the live SSE-merged marker positions
    // (:file:`snapshot-layer.tsx` keeps them in ``featureCollectionRef``) with
    // the older REST-snapshot positions — snapping every marker BACK along its
    // track tail until the next SSE delta snapped it forward again (the
    // reported glyph flicker). See :func:`SnapshotLayer`'s split effects.
    existingSource.setData(featureCollection);
    reapplySnapshotPlatformStyle(map, platformColor, platformGlyph);
    return;
  }

  // Source is configured with native MapLibre clustering enabled
  // per ADR-016 Open Item 5 MVP default — features at zoom <=
  // :const:`CLUSTER_MAX_ZOOM` collapse into a cluster feature;
  // zoom 7+ resolves to individual markers (REQ-UI-011). The
  // per-status counters configured via :func:`clusterProperties`
  // feed the cluster layer's dominant-status paint expression
  // (see :file:`cluster-layer.tsx`).
  //
  // Cross-references:
  //   - REQ-UI-010 / REQ-UI-011.
  //   - ADR-016 Open Item 5 + §2 Stage 4 + §4.
  //   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md.
  const iconImage = glyphIconImage(normalizeGlyphId(platformGlyph));
  map.addSource(SNAPSHOT_SOURCE_ID, {
    type: "geojson",
    data: featureCollection,
    cluster: true,
    clusterMaxZoom: CLUSTER_MAX_ZOOM,
    clusterRadius: CLUSTER_RADIUS_PX,
    clusterProperties: clusterProperties(),
  });
  // The snapshot layer renders **individual markers only** — clustered
  // features are picked up by the sibling cluster layer in
  // :file:`cluster-layer.tsx`. ``filter: ["!", ["has", "point_count"]]``
  // excludes aggregated features so the snapshot's glyph layer paints
  // only un-clustered Producers (REQ-UI-011 — z 7+ individuals).
  map.addLayer({
    id: SNAPSHOT_LAYER_ID,
    type: "symbol",
    source: SNAPSHOT_SOURCE_ID,
    filter: ["!", ["has", "point_count"]],
    layout: {
      "icon-image": iconImage,
      "icon-size": iconSizeExpression(MARKER_RADIUS_ZOOM_STOPS),
      // Markers must always render + stay clickable (selection edge); never
      // collide a platform marker away.
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
    paint: {
      "icon-color": composeColorExpression(platformColor),
      // "Open circle for unknown" → ghosted glyph (see
      // :func:`composeIconOpacityExpression`); the grey tint carries the rest.
      "icon-opacity": composeIconOpacityExpression(),
      // CDS-replicated producers (issue #278, REQ-UI-014) get a coloured SDF
      // halo as the provenance marker — the symbol-layer analogue of the prior
      // 3 px stroke ring (an SDF has no stroke channel; the halo is the
      // tintable edge). Same status colour as the glyph so it reads as a ring
      // around the marker, not a second hue. ``0`` halo for non-CDS markers.
      // The polished visual is ADR-016 Open Item 12 (sponsor).
      "icon-halo-color": composeColorExpression(platformColor),
      "icon-halo-width": ["case", ["get", "crossed_cds"], 2, 0],
    },
  });

  // Selection ring on top of the marker — stroke-only, filtered to the
  // selected (un-clustered) Producer. Shares the snapshot source and uses
  // the same ring as the event glyph (REQ-UI-051; storyboard Flow Q
  // §"Frame 2"). Added before the label layer so the operator-facing name
  // stays on top; added even when nothing is selected (the sentinel filter
  // paints nothing) so the ring is ready the moment a selection arrives.
  map.addLayer({
    id: SNAPSHOT_HIGHLIGHT_LAYER_ID,
    type: "circle",
    source: SNAPSHOT_SOURCE_ID,
    filter: platformHighlightFilter(null),
    paint: {
      "circle-radius": selectionRingRadiusExpression(MARKER_RADIUS_ZOOM_STOPS),
      "circle-color": "#000000",
      "circle-opacity": 0,
      "circle-stroke-width": SELECTION_RING_STROKE_WIDTH,
      "circle-stroke-color": getSelectionRingColor(),
      "circle-stroke-opacity": SELECTION_RING_STROKE_OPACITY,
    },
  });

  // Per-glyph display-name label, centered below the marker. The label
  // text is the operator-facing display name the BFF snapshot joins from
  // the control-plane registry (producer_id is the canonical UUID); an
  // empty ``display_name`` (producer not yet registered) places no label.
  //
  // - Shares the snapshot source + ``filter: !has point_count`` so labels
  //   render only for individual (un-clustered) glyphs; ``minzoom`` past
  //   CLUSTER_MAX_ZOOM means labels appear exactly when markers decluster
  //   (z7+), matching Flow 03 "default zoom = dots only" + the ADR-016
  //   1000-marker readability/perf posture.
  // - ``text-anchor: "top"`` + a downward ``text-offset`` seats the text
  //   below the circle; collision is on (``text-allow-overlap: false``)
  //   with ``text-optional`` so dense areas thin labels but never drop the
  //   glyph itself.
  // - ``text-font`` matches the BFF glyph atlas's fontstack (issue #205);
  //   color + halo resolve from theme tokens at paint time (REQ-UI-040/046).
  map.addLayer({
    id: SNAPSHOT_LABEL_LAYER_ID,
    type: "symbol",
    source: SNAPSHOT_SOURCE_ID,
    filter: ["!", ["has", "point_count"]],
    minzoom: CLUSTER_MAX_ZOOM + 1,
    layout: {
      "text-field": ["get", "display_name"],
      "text-font": ["DM Sans Medium"],
      "text-size": SNAPSHOT_LABEL_SIZE_PX,
      "text-anchor": "top",
      // ems relative to text-size; clears the marker radius (≤ 8 px at
      // z14, MARKER_RADIUS_ZOOM_STOPS) with a small gap.
      "text-offset": [0, 0.9],
      "text-optional": true,
      "text-allow-overlap": false,
      "text-max-width": 12,
    },
    paint: {
      "text-color": getLabelTextColor(),
      "text-halo-color": getLabelHaloColor(),
      "text-halo-width": SNAPSHOT_LABEL_HALO_WIDTH_PX,
    },
  });
}

/**
 * Re-apply the platform marker's **glyph shape + colour in place** — paint /
 * layout only, **never** ``setData`` — so a Profile colour-mode / single-colour
 * / glyph change (or a theme flip) repaints the live marker layer without
 * disturbing its source data (REQ-UI-086 / REQ-UI-040).
 *
 * # Why this is separate from :func:`attachSnapshotLayer` (the flicker fix)
 *
 * The snapshot source carries the **live SSE-merged** marker positions
 * (:file:`snapshot-layer.tsx` accumulates status deltas into
 * ``featureCollectionRef`` and writes them via ``setData``). A colour/glyph
 * change must NOT re-run ``setData`` with the older REST-snapshot positions —
 * doing so snapped every marker BACK along its track tail (to the snapshot
 * point) until the next SSE delta snapped it forward, the reported glyph
 * flicker. So the :class:`SnapshotLayer`'s colour/glyph effect calls this
 * paint-only path; only a genuine snapshot DATA change goes through
 * :func:`attachSnapshotLayer` (which owns ``setData``).
 *
 * ``icon-image`` is re-applied via :meth:`Map.setLayoutProperty` **only when
 * the glyph id actually changed** — MapLibre re-buckets a symbol layer on an
 * ``icon-image`` layout write, so re-setting it to the same value is avoided
 * (belt-and-braces; MapLibre also deep-equals layout values, but we never want
 * to depend on that for placement stability). ``icon-color`` +
 * ``icon-halo-color`` (the CDS-provenance halo shares the glyph hue) are pure
 * paint and re-applied unconditionally — cheap and never re-place symbols.
 *
 * No-op when the map is removed / mid-teardown, or the layer isn't registered
 * yet (defensive — the source can exist a tick before the layer in a remount
 * race; and a still-loading map has no layer yet, so the ``getLayer`` guard
 * below short-circuits until the first attach runs).
 *
 * @param map - The live MapLibre map.
 * @param platformColor - Active Profile platform colour config (mode + single).
 * @param platformGlyph - Active Profile platform glyph id (unknown → circle).
 *
 * Cross-references:
 *   - REQ-UI-086 (platform colour + glyph); ADR-033 §9.2.
 *   - REQ-UI-040 (theme flip re-paint seam).
 *   - REQ-UI-006 (live SSE positions the source must keep).
 */
export function reapplySnapshotPlatformStyle(
  map: MapLibreMap,
  platformColor?: PlatformColorConfig,
  platformGlyph?: string,
): void {
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(SNAPSHOT_LAYER_ID) === undefined) {
    return;
  }
  // Only re-set ``icon-image`` when the glyph id changed (avoids re-bucketing
  // the symbol layer + any icon re-fade for a colour-only change).
  const nextIconImage = glyphIconImage(normalizeGlyphId(platformGlyph));
  const currentIconImage = map.getLayoutProperty(SNAPSHOT_LAYER_ID, "icon-image");
  if (currentIconImage !== nextIconImage) {
    map.setLayoutProperty(SNAPSHOT_LAYER_ID, "icon-image", nextIconImage);
  }
  const colorExpr = composeColorExpression(platformColor);
  map.setPaintProperty(SNAPSHOT_LAYER_ID, "icon-color", colorExpr);
  // The CDS-provenance halo is the same colour as the glyph.
  map.setPaintProperty(SNAPSHOT_LAYER_ID, "icon-halo-color", colorExpr);
}

/**
 * Point the selection-highlight ring at ``producerId`` (or clear it when
 * ``null``) via a ``setFilter`` call. No-op when the layer isn't registered
 * yet. Mirrors :func:`detection-layer-impl.setSelectedDetectionHighlight`.
 */
export function setSelectedPlatformHighlight(map: MapLibreMap, producerId: string | null): void {
  // No-op safely on a removed / not-ready map — ``getLayer`` would otherwise
  // throw on ``map.style`` during a teardown race (see :file:`map/map-ready.ts`).
  if (!isMapUsable(map)) {
    return;
  }
  if (!map.getLayer(SNAPSHOT_HIGHLIGHT_LAYER_ID)) {
    return;
  }
  map.setFilter(SNAPSHOT_HIGHLIGHT_LAYER_ID, platformHighlightFilter(producerId));
}

/**
 * Tear down the layers + source on unmount.
 *
 * Calls ``removeLayer`` before ``removeSource`` per MapLibre's
 * documented ordering (a source can't be removed while a layer
 * references it).
 *
 * Cross-references:
 *   - REQ-UI-002.
 */
export function detachSnapshotLayer(map: MapLibreMap): void {
  // No-op safely on a removed / not-ready map. On the "Reset to profile" /
  // panel-rebuild path the Map unmounts and ``map.remove()`` deletes the style;
  // the ``getLayer`` guards below dereference ``map.style`` and would otherwise
  // throw ``Cannot read properties of undefined (reading 'getLayer')`` (the
  // reported crash). See :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  // Remove all layers (label + highlight + circle) before the shared source.
  if (map.getLayer(SNAPSHOT_LABEL_LAYER_ID)) {
    map.removeLayer(SNAPSHOT_LABEL_LAYER_ID);
  }
  if (map.getLayer(SNAPSHOT_HIGHLIGHT_LAYER_ID)) {
    map.removeLayer(SNAPSHOT_HIGHLIGHT_LAYER_ID);
  }
  if (map.getLayer(SNAPSHOT_LAYER_ID)) {
    map.removeLayer(SNAPSHOT_LAYER_ID);
  }
  if (map.getSource(SNAPSHOT_SOURCE_ID)) {
    map.removeSource(SNAPSHOT_SOURCE_ID);
  }
}

/**
 * Sanity-check helper: confirm the design-system fallback hex map
 * covers every status used by :func:`composeColorExpression`. The
 * test in :file:`snapshot-layer.test.tsx` calls this so the
 * implementation file is the single source of truth for which
 * statuses contribute to the paint spec.
 */
export function paintExpressionStatusCoverage(): ReadonlyArray<
  keyof typeof STATUS_FALLBACK_HEX_MAP
> {
  return ["healthy", "degraded", "unhealthy", "unknown"];
}
